/**
 * Floating cross-chat widget — sits in the bottom-right corner on every
 * page. Two tabs:
 *   - "Bridge": 3-way bridge between game + Discord + web. Posting
 *     requires a logged-in user AND a linked in-game character on the
 *     selected server. Reading is open to everyone.
 *   - "Lobby": web-only room. Anonymous visitors choose a nickname.
 *
 * UX rules:
 *   - Collapsed: compact bubble showing "Chat" + unread dot.
 *   - Expanded desktop: 380×500 floating card pinned bottom-right.
 *   - Expanded mobile (<640 px): full-screen takeover so the keyboard
 *     doesn't squeeze the messages out of view.
 *   - Persistent: open/closed state stored in sessionStorage.
 *
 * Real-time via /api/chat/ws WebSocket; falls back to a stale REST
 * snapshot on connect.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Users } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = (BACKEND_URL || "").replace(/^http/, "ws");
const NICK_STORAGE_KEY = "chillet_lobby_nick";

// Each entry is `{ label (shown in dropdown), value (sent to backend) }`.
// The DB stores `Chillet's Revival Pvpve` (lower-case "pvpve") — that's the
// canonical server name, and ALL link/RCON lookups key off it. But we want
// the dropdown to read "PvPvE" because that's what your fan-art / brand
// uses, so label and value diverge for the Revival server.
const SERVER_OPTIONS = [
  { label: "Chillet's Modded Demise", value: "Chillet's Modded Demise", color: "#FF6B35" },
  { label: "Chillet's Revival PvPvE", value: "Chillet's Revival Pvpve", color: "#00E5FF" },
];

function fmtTime(iso) {
  try {
    const d = new Date(iso.replace(" ", "T") + "Z");
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function MessageRow({ msg, channel }) {
  const { isMaster } = useMasterTrainers();
  // A resolved avatar can still 404 (deleted Discord asset) — fall back
  // to the letter badge instead of rendering a broken image box.
  const [avatarBroken, setAvatarBroken] = useState(false);
  useEffect(() => setAvatarBroken(false), [msg.avatar_url]);
  const sourceColor =
    msg.source === "discord"
      ? "#5865F2"
      : msg.source === "game"
      ? "#FFD700"
      : "#00E5FF";
  // Look up the friendly label for the server (typo-correcting "Pvpve" → "PvPvE")
  const serverLabel =
    SERVER_OPTIONS.find((s) => s.value === msg.server_name)?.label ||
    msg.server_name;
  return (
    <div className="px-3 py-1.5 text-sm flex gap-2" data-testid="chat-message">
      {/* Avatar circle — for web messages we show the Discord/default
          avatar from the row; for game/discord messages we render a
          tiny coloured dot matching the source label. */}
      {msg.avatar_url && !avatarBroken ? (
        <img
          src={msg.avatar_url}
          alt=""
          className="w-5 h-5 rounded-full mt-0.5 shrink-0"
          style={{ border: `1px solid ${sourceColor}55` }}
          loading="lazy"
          onError={() => setAvatarBroken(true)}
          data-testid="chat-message-avatar"
        />
      ) : (
        <div
          className="w-5 h-5 rounded-full mt-0.5 shrink-0 flex items-center justify-center text-[9px] font-bold"
          style={{
            background: `${sourceColor}1f`,
            color: sourceColor,
            border: `1px solid ${sourceColor}55`,
          }}
        >
          {String(msg.author_name || "?").trim().charAt(0).toUpperCase() || "?"}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span
            className="text-[10px] uppercase tracking-wider shrink-0"
            style={{ color: sourceColor }}
          >
            [{msg.source}]
          </span>
          <span
            className="text-[var(--bone)] font-medium truncate"
            style={msg.author_color ? { color: msg.author_color } : undefined}
            data-testid="chat-author-name"
          >
            {msg.author_name}
          </span>
          {isMaster(msg.author_name) && (
            <span
              className="text-[var(--gold)] text-[10px] leading-none shrink-0"
              title="Master Trainer — defeated all 9 NPC trainers"
              data-testid="chat-master-trainer-star"
            >
              ★
            </span>
          )}
          {channel === "bridge" && msg.server_name && (
            <span className="text-[10px] text-[var(--muted)] truncate">
              · {serverLabel.replace(/Chillet's /, "")}
            </span>
          )}
          <span className="text-[10px] text-[var(--muted)] ml-auto shrink-0">
            {fmtTime(msg.created_at)}
          </span>
        </div>
        <div className="text-[var(--bone)] break-words" style={{ wordBreak: "break-word" }}>
          {msg.message}
        </div>
      </div>
    </div>
  );
}

function useChatChannel({ channel, serverName }) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setMessages([]);

    // Initial snapshot
    api
      .get(
        `/chat/recent?channel=${channel}` +
          (channel === "bridge" && serverName ? `&server_name=${encodeURIComponent(serverName)}` : ""),
      )
      .then(({ data }) => {
        if (!alive) return;
        setMessages(data?.messages || []);
      })
      .catch(() => {});

    // Live socket
    const ws = new WebSocket(`${WS_URL}/api/chat/ws?channel=${channel}`);
    wsRef.current = ws;
    ws.onopen = () => alive && setConnected(true);
    ws.onclose = () => alive && setConnected(false);
    ws.onerror = () => alive && setConnected(false);
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (m.type !== "message") return;
        // Bridge clients filter by selected server. Lobby has no server.
        if (channel === "bridge" && serverName && m.server_name && m.server_name !== serverName) {
          return;
        }
        setMessages((prev) => [...prev.slice(-199), m]);
      } catch {
        /* ignore */
      }
    };

    return () => {
      alive = false;
      try {
        ws.close();
      } catch {
        /* noop */
      }
    };
  }, [channel, serverName]);

  return { messages, connected };
}

/** Live presence — polls /api/chat/presence every 30 s. Returns null
 * while loading; the parent then decides whether to show a sidebar. */
function usePresence(serverName, enabled) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled || !serverName) {
      setData(null);
      return;
    }
    let alive = true;
    const poll = async () => {
      try {
        const { data: d } = await api.get(
          `/chat/presence?server_name=${encodeURIComponent(serverName)}`,
        );
        if (alive) setData(d);
      } catch {
        /* keep last good */
      }
    };
    poll();
    const id = setInterval(poll, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [serverName, enabled]);
  return data;
}

export default function ChatWidget() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // The map owns its bottom-right corner (Leaflet zoom control lives
  // there), so the collapsed bubble swaps sides on that route.
  const onMap = pathname === "/map" || pathname.startsWith("/map/");
  const [open, setOpen] = useState(() => sessionStorage.getItem("chat_open") === "1");
  const [tab, setTab] = useState("lobby");
  const [serverName, setServerName] = useState(SERVER_OPTIONS[0].value);
  const [draft, setDraft] = useState("");
  const [nick, setNick] = useState(() => sessionStorage.getItem(NICK_STORAGE_KEY) || "");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);

  const { messages, connected } = useChatChannel({
    channel: tab,
    serverName: tab === "bridge" ? serverName : null,
  });

  // Bridge-only sidebar: live in-game player list. Drives the toggleable
  // "Online" sidebar so chatters can see who's actually in-game.
  const [showPresence, setShowPresence] = useState(false);
  const presence = usePresence(serverName, tab === "bridge" && open);

  // Auto-scroll on new messages when the panel is visible
  useEffect(() => {
    if (!open) {
      // Track unread while collapsed
      setUnread((u) => Math.min(99, u + 1));
      return;
    }
    setUnread(0);
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    sessionStorage.setItem("chat_open", open ? "1" : "0");
    if (open) setUnread(0);
  }, [open]);

  useEffect(() => {
    if (nick) sessionStorage.setItem(NICK_STORAGE_KEY, nick);
  }, [nick]);

  const canPostBridge = !!user;
  const placeholder = useMemo(() => {
    if (tab === "lobby") return user ? "Say something…" : "Type a nickname above, then chat";
    if (!canPostBridge) return "Sign in to send to the bridge";
    return `Send to ${serverName} (game + Discord)…`;
  }, [tab, user, canPostBridge, serverName]);

  async function send() {
    setError("");
    const msg = draft.trim();
    if (!msg || sending) return;
    if (tab === "bridge" && !canPostBridge) {
      setError("Sign in to chat on the bridge.");
      return;
    }
    if (tab === "lobby" && !user && !nick.trim()) {
      setError("Pick a nickname to chat anonymously.");
      return;
    }
    setSending(true);
    try {
      const payload = { channel: tab, message: msg };
      if (tab === "bridge") payload.server_name = serverName;
      if (tab === "lobby" && !user) payload.nickname = nick.trim();
      await api.post("/chat/send", payload);
      setDraft("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to send.");
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="chat-widget-open"
        // On the map the bottom-RIGHT corner belongs to Leaflet's zoom
        // control, so the bubble moves to the left corner there rather
        // than covering the zoom buttons.
        className={`fixed z-[1000] flex items-center gap-2 px-4 py-2.5 rounded-full border bg-black/80 backdrop-blur-md border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.1)] transition-all ${
          onMap ? "left-4 right-auto" : "right-4"
        }`}
        style={{
          boxShadow: "0 0 18px rgba(0,229,255,0.35)",
          // Sit above the Emergent preview badge in the corner so the bubble
          // is always clickable in the preview env. In production this is
          // still flush with the corner — extra padding is harmless.
          bottom: "calc(1rem + 56px)",
        }}
      >
        <MessageCircle size={16} className="text-[var(--teal)]" />
        <span className="text-xs uppercase tracking-[0.14em] text-[var(--bone)]">Chat</span>
        {unread > 0 && (
          <span
            className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full"
            style={{ background: "#E63946", color: "#fff" }}
          >
            {unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="fixed z-[1000] flex flex-col border border-[var(--teal-soft)] bg-black/85 backdrop-blur-md text-[var(--bone)] shadow-2xl"
      data-testid="chat-widget"
      style={{
        bottom: "calc(1rem + 56px)",
        right: "1rem",
        width: "min(380px, calc(100vw - 2rem))",
        // `dvh` so the panel measures the visible viewport — with `vh` the
        // composer at the bottom ended up under the mobile URL bar.
        height: "min(520px, calc(100dvh - 6rem))",
        borderRadius: "0.75rem",
        boxShadow: "0 0 32px rgba(0,229,255,0.25)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <MessageCircle size={14} className="text-[var(--teal)]" />
          <span className="text-xs uppercase tracking-[0.16em]">Chat</span>
          <span
            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
              connected ? "text-emerald-300" : "text-[var(--muted)]"
            }`}
            style={{ background: connected ? "rgba(52,211,153,0.1)" : "rgba(120,120,120,0.1)" }}
          >
            {connected ? "live" : "offline"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="chat-widget-close"
          className="text-[var(--muted)] hover:text-[var(--bone)]"
          aria-label="Close chat"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {[
          { id: "lobby", label: "Lobby" },
          { id: "bridge", label: "Bridge" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`chat-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 text-xs uppercase tracking-[0.14em] py-2 transition-colors ${
              tab === t.id
                ? "text-[var(--teal)] border-b-2 border-[var(--teal)]"
                : "text-[var(--muted)] hover:text-[var(--bone)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Bridge: server picker + presence toggle */}
      {tab === "bridge" && (
        <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
          <Users size={12} className="text-[var(--muted)] shrink-0" />
          <select
            data-testid="chat-server-picker"
            value={serverName}
            onChange={(e) => setServerName(e.target.value)}
            className="flex-1 bg-black/40 border border-white/10 text-[var(--bone)] text-xs px-2 py-1 rounded"
          >
            {SERVER_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowPresence((v) => !v)}
            data-testid="chat-presence-toggle"
            title="Show players currently in-game"
            className={`flex items-center gap-1 px-1.5 py-1 rounded text-[10px] uppercase tracking-wider transition-colors border ${
              showPresence
                ? "border-[var(--teal-soft)] text-[var(--teal)] bg-[rgba(0,229,255,0.08)]"
                : "border-white/10 text-[var(--muted)] hover:text-[var(--bone)]"
            }`}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: presence?.online && presence.count > 0 ? "#34d399" : "#666" }}
            />
            {presence ? presence.count : "—"}
          </button>
        </div>
      )}

      {/* Messages + optional presence sidebar (Bridge only) */}
      <div className="flex-1 flex min-h-0">
        <div
          ref={scrollRef}
          data-testid="chat-messages"
          className="flex-1 overflow-y-auto py-2"
          style={{ scrollBehavior: "smooth" }}
        >
          {messages.length === 0 && (
            <div className="text-center text-xs text-[var(--muted)] py-6 px-4">
              {tab === "bridge"
                ? "No messages yet. Bridge chat shows game + Discord + web traffic for the selected server."
                : "No messages yet. Pick a nickname and say hi 🪙"}
            </div>
          )}
          {messages.map((m) => (
            <MessageRow key={m.id} msg={m} channel={tab} />
          ))}
        </div>
        {tab === "bridge" && showPresence && (
          <aside
            data-testid="chat-presence-list"
            className="w-32 border-l border-white/10 overflow-y-auto py-2 px-2 shrink-0"
            style={{ background: "rgba(0,0,0,0.25)" }}
          >
            <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--muted)] mb-2 flex items-center justify-between">
              <span>Online</span>
              <span className="text-[var(--bone)]">{presence?.count ?? "—"}</span>
            </div>
            {presence && presence.players.length === 0 && (
              <div className="text-[10px] text-[var(--muted)] italic">
                No players in-game
              </div>
            )}
            {presence?.players.map((p) => (
              <div
                key={p.name}
                className="text-[11px] text-[var(--bone)] mb-1 truncate"
                title={`${p.name} · Level ${p.level}`}
              >
                <span
                  className="inline-block w-1 h-1 rounded-full mr-1.5 align-middle"
                  style={{ background: "#34d399" }}
                />
                {p.name}
                {p.level > 0 && (
                  <span className="text-[9px] text-[var(--muted)] ml-1">
                    L{p.level}
                  </span>
                )}
              </div>
            ))}
          </aside>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-white/10 p-2 space-y-2">
        {tab === "lobby" && !user && (
          <input
            data-testid="chat-lobby-nick"
            type="text"
            value={nick}
            onChange={(e) => setNick(e.target.value.slice(0, 24))}
            placeholder="Your nickname (anonymous)"
            className="w-full bg-black/40 border border-white/10 text-[var(--bone)] text-xs px-2 py-1.5 rounded outline-none focus:border-[var(--teal-soft)]"
          />
        )}
        {error && (
          <div
            data-testid="chat-error"
            className="text-[11px] text-red-400 px-1"
          >
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <input
            data-testid="chat-input"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 280))}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={tab === "bridge" && !canPostBridge}
            className="flex-1 bg-black/40 border border-white/10 text-[var(--bone)] text-sm px-3 py-2 rounded outline-none focus:border-[var(--teal-soft)] disabled:opacity-40"
          />
          <button
            type="button"
            data-testid="chat-send"
            onClick={send}
            disabled={!draft.trim() || sending || (tab === "bridge" && !canPostBridge)}
            className="px-3 rounded border border-[var(--teal-soft)] text-[var(--teal)] hover:bg-[rgba(0,229,255,0.08)] disabled:opacity-40"
            aria-label="Send"
          >
            <Send size={14} />
          </button>
        </div>
        <div className="text-[10px] text-[var(--muted)] px-1">
          {tab === "bridge"
            ? canPostBridge
              ? "Sends to game + Discord. Requires a linked in-game character."
              : "Bridge is read-only until you sign in & link your character."
            : "Lobby is web-only. Stays on the website."}
        </div>
      </div>
    </div>
  );
}
